import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import Skeleton from '@mui/material/Skeleton';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { alpha, type Theme, useTheme } from '@mui/material/styles';

import { useClientDevicesQuery, useIssuesQuery, useClientSummaryQuery } from '@/api/queries';
import type { DeviceIssue, DeviceRecord, IssueType, CoverageStatus } from '@/data/types';
import SeverityChip from '@/components/SeverityChip';
import CoverageBar from '@/components/CoverageBar';
import RetiredDeviceDetail from '@/components/RetiredDeviceDetail';
import ExportMenu from '@/components/ExportMenu';
import { useErrorSnackbar } from '@/components/ErrorSnackbar';
import CopyableText from '@/components/CopyableText';
import { buildDeviceExportRows } from '@/hooks/useExport';

const ISSUE_TYPE_LABELS: Record<IssueType, string> = {
  missing_huntress: 'Missing Huntress',
  missing_threatlocker: 'Missing ThreatLocker',
  missing_msp360: 'Missing MSP360',
  orphaned_device: 'Orphaned Device',
  av_unhealthy: 'AV Unhealthy',
  av_not_protected: 'AV Not Protected',
  retired_with_active_agents: 'Retired w/ Active Agents',
};

const STATUS_COLORS: Record<string, string> = {
  issue: 'error.main',
  orphaned: 'warning.main',
  healthy: 'success.main',
};

/** Shared DataGrid styling */
const dataGridSx = (t: Theme) => ({
  border: `1px solid ${t.palette.divider}`,
  borderRadius: 2.5,
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: alpha(t.palette.primary.main, 0.04),
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontFamily: '"Outfit", sans-serif',
    fontWeight: 600,
    fontSize: '0.68rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
  },
  '& .MuiDataGrid-cell': {
    borderColor: t.palette.divider,
  },
});

const issueColumns: GridColDef<DeviceIssue>[] = [
  {
    field: 'deviceName',
    headerName: 'Device',
    flex: 2,
    minWidth: 150,
    sortable: true,
    renderCell: (params) => <CopyableText text={String(params.value ?? '')} />,
  },
  {
    field: 'issueType',
    headerName: 'Issue Type',
    width: 180,
    sortable: true,
    valueFormatter: (value: IssueType) => ISSUE_TYPE_LABELS[value] ?? String(value),
  },
  {
    field: 'severity',
    headerName: 'Severity',
    width: 130,
    sortable: true,
    renderCell: (params) => (
      <SeverityChip
        severity={params.value as 'critical' | 'warning' | 'info'}
        label={String(params.value ?? 'info')}
      />
    ),
  },
  {
    field: 'affectedPlatform',
    headerName: 'Platform',
    width: 130,
    sortable: true,
    renderCell: (params) => (
      <Typography
        sx={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.75rem',
          fontWeight: 500,
        }}
      >
        {String(params.value ?? '')}
      </Typography>
    ),
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 2,
    minWidth: 200,
    sortable: false,
    renderCell: (params) => (
      <Typography
        sx={{
          fontSize: '0.8rem',
          color: 'text.secondary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {String(params.value ?? '')}
      </Typography>
    ),
  },
];

const PLATFORM_LABELS: Record<string, string> = {
  automate: 'Automate',
  addigy: 'Addigy',
  huntress: 'Huntress',
  threatlocker: 'ThreatLocker',
  msp360: 'MSP360',
};

/** Names other platforms report for this device, when they differ from the row name. */
function otherPlatformNames(device: DeviceRecord): string[] {
  const squash = (name: string) => name.toLowerCase().replace(/[^a-z0-9]/g, '');
  const primary = squash(device.deviceName);
  const names = Object.values(device.platformNames ?? {}).filter((n) => squash(n) !== primary);
  return [...new Set(names)];
}

/** Tooltip body: every platform's name, how it was matched, and the serial. */
function matchDetails(device: DeviceRecord) {
  const entries = Object.entries(device.platformNames ?? {});
  if (entries.length === 0) return '';
  return (
    <Box sx={{ fontSize: '0.75rem' }}>
      {entries.map(([platform, name]) => (
        <div key={platform}>
          {PLATFORM_LABELS[platform] ?? platform}: {name}
          {device.matchTiers?.[platform]
            ? ` — matched by ${device.matchTiers[platform].replace('_', ' ')}`
            : ''}
        </div>
      ))}
      {device.serialNumber && <div>Serial: {device.serialNumber}</div>}
    </Box>
  );
}

const deviceColumns: GridColDef<DeviceRecord>[] = [
  {
    field: 'deviceName',
    headerName: 'Device',
    flex: 2,
    minWidth: 150,
    sortable: true,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <CopyableText text={String(params.value ?? '')} />
        {otherPlatformNames(params.row).length > 0 && (
          <Tooltip title={matchDetails(params.row)} placement="bottom-start">
            <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
              aka {otherPlatformNames(params.row).join(', ')}
            </Typography>
          </Tooltip>
        )}
        {params.row.isRetired && (
          <Chip
            label="Retired"
            size="small"
            sx={{
              height: 18,
              fontSize: '0.6rem',
              fontWeight: 600,
              bgcolor: (t: Theme) => alpha(t.palette.warning.main, 0.12),
              color: 'warning.main',
              border: (t: Theme) => `1px solid ${alpha(t.palette.warning.main, 0.3)}`,
            }}
          />
        )}
      </Box>
    ),
  },
  {
    field: 'locationName',
    headerName: 'Location',
    width: 160,
    sortable: true,
    type: 'singleSelect',
    valueFormatter: (value: string | undefined) => value || 'Unknown',
  },
  {
    field: 'osType',
    headerName: 'OS',
    width: 90,
    sortable: true,
    renderCell: (params) => (
      <Typography
        sx={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.7rem',
          color: 'text.secondary',
          textTransform: 'uppercase',
        }}
      >
        {String(params.value ?? '')}
      </Typography>
    ),
  },
  {
    field: 'platforms',
    headerName: 'Platforms',
    width: 300,
    sortable: false,
    renderCell: (params) => {
      const platforms = (params.value ?? {}) as Record<string, boolean>;
      return (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {Object.entries(platforms).map(([name, present]) => (
            <Chip
              key={name}
              label={name}
              size="small"
              sx={(t: Theme) => ({
                fontSize: '0.65rem',
                height: 20,
                fontWeight: 600,
                fontFamily: 'var(--font-mono)',
                ...(present
                  ? {
                      bgcolor: alpha(t.palette.success.main, 0.12),
                      color: 'success.main',
                      border: `1px solid ${alpha(t.palette.success.main, 0.2)}`,
                    }
                  : {
                      bgcolor: alpha(t.palette.error.main, 0.08),
                      color: 'error.main',
                      border: `1px solid ${alpha(t.palette.error.main, 0.15)}`,
                    }),
              })}
            />
          ))}
        </Box>
      );
    },
  },
  {
    field: 'lastSeen',
    headerName: 'Last Seen',
    width: 120,
    sortable: true,
    renderCell: (params) => {
      const d = new Date(params.value as string);
      const label = isNaN(d.getTime())
        ? '—'
        : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      return (
        <Typography
          sx={{
            fontFamily: 'var(--font-mono)',
            fontSize: '0.7rem',
            color: 'text.secondary',
          }}
        >
          {label}
        </Typography>
      );
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 110,
    sortable: true,
    renderCell: (params) => {
      const status = String(params.value ?? 'unknown');
      const color = STATUS_COLORS[status] ?? 'text.secondary';
      const paletteKey =
        status === 'healthy' ? 'success' : status === 'orphaned' ? 'warning' : 'error';
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: color,
              boxShadow: (t: Theme) => `0 0 6px ${alpha(t.palette[paletteKey].main, 0.5)}`,
            }}
          />
          <Typography
            sx={{
              fontSize: '0.7rem',
              fontWeight: 600,
              color,
              fontFamily: 'var(--font-mono)',
              textTransform: 'capitalize',
            }}
          >
            {status}
          </Typography>
        </Box>
      );
    },
  },
];

const DATA_GRID_COMMON = {
  density: 'compact' as const,
  pageSizeOptions: [25, 50, 100],
  initialState: {
    pagination: { paginationModel: { pageSize: 100 } },
  },
  disableRowSelectionOnClick: true,
};

export default function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [locationFilter, setLocationFilter] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<DeviceRecord | null>(null);
  const theme = useTheme();

  const {
    data: client,
    isLoading: clientLoading,
    isError: clientError,
  } = useClientSummaryQuery(id ?? '', { includeRetired: true });
  const {
    data: devices,
    isLoading: devicesLoading,
    isError: devicesError,
  } = useClientDevicesQuery(id ?? '', { includeRetired: true });
  const { data: issues, isError: issuesError } = useIssuesQuery(id);
  const { showError } = useErrorSnackbar();

  useEffect(() => {
    if (clientError || devicesError || issuesError) {
      showError('Failed to load device data');
    }
  }, [clientError, devicesError, issuesError, showError]);

  const allDevicesRaw = useMemo(() => devices ?? [], [devices]);
  const isAddigyClient = (client?.addigyCount ?? 0) > 0;

  /** Derive unique sorted location values for filter dropdown and column */
  const locationValueOptions = useMemo(() => {
    const locs = allDevicesRaw.map((d) => d.locationName).filter((l): l is string => Boolean(l));
    return [...new Set(locs)].sort((a, b) => a.localeCompare(b));
  }, [allDevicesRaw]);

  /** Filter helper: true when a device matches the selected location */
  const matchesLocation = useMemo(() => {
    if (!locationFilter) return () => true;
    return (d: { locationName?: string }) => (d.locationName || 'Unknown') === locationFilter;
  }, [locationFilter]);

  /** Lookup: device name → location, for filtering issues by location */
  const deviceLocationMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of allDevicesRaw) {
      map.set(d.deviceName, d.locationName || 'Unknown');
    }
    return map;
  }, [allDevicesRaw]);

  const allDevices = useMemo(
    () => allDevicesRaw.filter((d) => !d.isRetired).filter(matchesLocation),
    [allDevicesRaw, matchesLocation]
  );
  const retiredDevices = useMemo(
    () => allDevicesRaw.filter((d) => d.isRetired).filter(matchesLocation),
    [allDevicesRaw, matchesLocation]
  );
  const retiredCount = retiredDevices.length;

  const exportData = useMemo(
    () => buildDeviceExportRows(allDevicesRaw.filter(matchesLocation), true),
    [allDevicesRaw, matchesLocation]
  );
  const exportFilename = (client?.companyName ?? client?.clientName ?? 'client-devices')
    .replace(/[^a-zA-Z0-9-_ ]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase();

  const rawIssues = issues ?? [];
  const allIssues = useMemo(() => {
    const mapped = isAddigyClient
      ? rawIssues.map((i) =>
          i.issueType === 'orphaned_device' ? { ...i, affectedPlatform: 'Addigy' } : i
        )
      : rawIssues;
    if (!locationFilter) return mapped;
    return mapped.filter((i) => deviceLocationMap.get(i.deviceName) === locationFilter);
  }, [rawIssues, isAddigyClient, locationFilter, deviceLocationMap]);
  const orphaned = allDevices.filter((d) => d.status === 'orphaned');

  if ((clientLoading || devicesLoading) && !client) {
    return (
      <Box sx={{ py: 2 }}>
        <Skeleton variant="text" width={120} height={30} sx={{ mb: 2 }} />
        <Skeleton variant="text" width={250} height={40} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={400} />
      </Box>
    );
  }

  if (!clientLoading && !client) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/clients')} sx={{ mb: 3 }}>
          Back to Clients
        </Button>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Client not found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data found for client ID: {id}
        </Typography>
      </Box>
    );
  }

  const issueCount = allIssues.length;
  const criticalCount = allIssues.filter((i) => i.severity === 'critical').length;
  const coveragePct = client?.coveragePct ?? 0;
  const coverageStatus: CoverageStatus =
    coveragePct >= 95 ? 'healthy' : coveragePct >= 80 ? 'warning' : 'critical';

  return (
    <Box>
      {/* Back button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/clients')}
        size="small"
        sx={{ mb: 1.5, color: 'text.secondary' }}
      >
        Clients
      </Button>

      {/* Client header */}
      <Card
        sx={{
          mb: 3,
          borderColor:
            coverageStatus === 'critical'
              ? alpha(theme.palette.error.main, 0.2)
              : coverageStatus === 'warning'
                ? alpha(theme.palette.warning.main, 0.15)
                : alpha(theme.palette.success.main, 0.12),
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ mb: 0.5 }}>
                {client?.companyName || client?.clientName}
              </Typography>
              {client?.companyName && (
                <Typography
                  sx={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.75rem',
                    color: 'text.secondary',
                    mb: 1,
                  }}
                >
                  {client.clientName}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                <Chip
                  label={`${client?.totalDevices ?? allDevices.length} devices`}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem' }}
                />
                {retiredCount > 0 && (
                  <Chip
                    label={`Retired: ${retiredCount}`}
                    size="small"
                    onClick={() => setActiveTab(3)}
                    sx={{
                      cursor: 'pointer',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      bgcolor: alpha(theme.palette.warning.main, activeTab === 3 ? 0.25 : 0.08),
                      color: 'warning.main',
                      border: `1px solid ${alpha(
                        theme.palette.warning.main,
                        activeTab === 3 ? 0.6 : 0.2
                      )}`,
                      ...(activeTab === 3 && {
                        boxShadow: `0 0 8px ${alpha(theme.palette.warning.main, 0.3)}`,
                      }),
                    }}
                  />
                )}
                {issueCount > 0 && (
                  <Chip
                    label={`${issueCount} issue${issueCount !== 1 ? 's' : ''}${criticalCount > 0 ? ` (${criticalCount} critical)` : ''}`}
                    size="small"
                    onClick={() => setActiveTab(0)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: alpha(theme.palette.error.main, activeTab === 0 ? 0.25 : 0.1),
                      color: 'error.main',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      border: `1px solid ${alpha(
                        theme.palette.error.main,
                        activeTab === 0 ? 0.6 : 0.2
                      )}`,
                      ...(activeTab === 0 && {
                        boxShadow: `0 0 8px ${alpha(theme.palette.error.main, 0.3)}`,
                      }),
                    }}
                  />
                )}
                {issueCount === 0 && (
                  <Chip
                    label="No issues"
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      color: 'success.main',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    }}
                  />
                )}
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right', minWidth: 160 }}>
              <Typography
                sx={{
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 700,
                  fontSize: '1.6rem',
                  color:
                    coverageStatus === 'critical'
                      ? 'error.main'
                      : coverageStatus === 'warning'
                        ? 'warning.main'
                        : 'success.main',
                }}
              >
                {coveragePct}%
              </Typography>
              <CoverageBar value={coveragePct} showLabel={false} height={4} />
              <Box sx={{ mt: 1.5 }}>
                <ExportMenu
                  data={exportData}
                  filenameBase={exportFilename}
                  disabled={allDevicesRaw.length === 0}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Card>

      {/* Tab bar */}
      <Tabs
        value={activeTab}
        onChange={(_, newValue: number) => setActiveTab(newValue)}
        sx={{
          mb: 2,
          '& .MuiTabs-indicator': {
            height: 2,
            borderRadius: 1,
          },
        }}
      >
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Issues
              {allIssues.length > 0 && (
                <Chip
                  label={allIssues.length}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    color: 'error.main',
                  }}
                />
              )}
            </Box>
          }
        />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              All Devices
              <Chip
                label={allDevices.length}
                size="small"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }}
              />
            </Box>
          }
        />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Orphaned
              {orphaned.length > 0 && (
                <Chip
                  label={orphaned.length}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    color: 'warning.main',
                  }}
                />
              )}
            </Box>
          }
        />
        <Tab
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Retired
              {retiredCount > 0 && (
                <Chip
                  label={retiredCount}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                    color: 'warning.dark',
                  }}
                />
              )}
            </Box>
          }
        />
      </Tabs>

      {/* Location filter toolbar */}
      {locationValueOptions.length > 1 && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Autocomplete
            value={locationFilter}
            onChange={(_, value) => setLocationFilter(value)}
            options={[...locationValueOptions, 'Unknown']}
            size="small"
            sx={{ width: 260 }}
            renderInput={(params) => (
              <TextField {...params} label="Filter by location" placeholder="All locations" />
            )}
          />
          {locationFilter && (
            <Button
              size="small"
              onClick={() => setLocationFilter(null)}
              sx={{ textTransform: 'none' }}
            >
              Clear
            </Button>
          )}
        </Box>
      )}

      {/* Tab panels */}
      {activeTab === 0 && (
        <DataGrid
          rows={allIssues}
          columns={issueColumns}
          getRowId={(row) => row.id}
          {...DATA_GRID_COMMON}
          sx={dataGridSx(theme)}
        />
      )}

      {activeTab === 1 && (
        <DataGrid
          rows={allDevices}
          columns={deviceColumns.map((col) =>
            col.field === 'locationName'
              ? { ...col, valueOptions: [...locationValueOptions, 'Unknown'] }
              : col
          )}
          getRowId={(row) => row.id}
          {...DATA_GRID_COMMON}
          sx={dataGridSx(theme)}
        />
      )}

      {activeTab === 2 && (
        <DataGrid
          rows={orphaned}
          columns={deviceColumns.map((col) =>
            col.field === 'locationName'
              ? { ...col, valueOptions: [...locationValueOptions, 'Unknown'] }
              : col
          )}
          getRowId={(row) => row.id}
          {...DATA_GRID_COMMON}
          sx={dataGridSx(theme)}
        />
      )}

      {activeTab === 3 && (
        <>
          <DataGrid
            rows={retiredDevices}
            columns={deviceColumns.map((col) =>
              col.field === 'locationName'
                ? { ...col, valueOptions: [...locationValueOptions, 'Unknown'] }
                : col
            )}
            getRowId={(row) => row.id}
            onRowClick={(params) => {
              setSelectedDevice(selectedDevice?.id === params.row.id ? null : params.row);
            }}
            {...DATA_GRID_COMMON}
            sx={{
              ...dataGridSx(theme),
              '& .MuiDataGrid-row': { opacity: 0.7 },
            }}
          />
          {selectedDevice?.isRetired && (
            <Box sx={{ mt: 2 }}>
              <RetiredDeviceDetail device={selectedDevice} clientName={client?.clientName ?? ''} />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
